import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  History, Clock, User, ArrowLeft, RotateCcw, GitCompare, 
  Plus, Save, X, FileText, DollarSign, ArrowUp, ArrowDown
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuoteVersionHistory = ({ quoteId, currentQuote, onVersionRestored, onClose }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVersions, setSelectedVersions] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [showNewVersion, setShowNewVersion] = useState(false);
  const [newVersionData, setNewVersionData] = useState({
    change_reason: '',
    change_summary: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadVersions();
  }, [quoteId]);

  const loadVersions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/quotes/${quoteId}/versions`);
      setVersions(response.data.versions);
    } catch (error) {
      console.error('Failed to load versions:', error);
      toast.error('Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVersion = async () => {
    if (!newVersionData.change_reason) {
      toast.error('Please provide a reason for this version');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/quotes/${quoteId}/versions`, newVersionData);
      toast.success('Version created successfully');
      setShowNewVersion(false);
      setNewVersionData({ change_reason: '', change_summary: '' });
      loadVersions();
    } catch (error) {
      console.error('Failed to create version:', error);
      toast.error('Failed to create version');
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreVersion = async (versionId, versionNumber) => {
    if (!confirm(`Are you sure you want to restore to version ${versionNumber}? Current quote will be backed up automatically.`)) {
      return;
    }

    try {
      const response = await axios.post(`${API}/quotes/${quoteId}/versions/${versionId}/restore`);
      toast.success(`Quote restored to version ${versionNumber}`);
      loadVersions();
      if (onVersionRestored) {
        onVersionRestored();
      }
    } catch (error) {
      console.error('Failed to restore version:', error);
      toast.error('Failed to restore version');
    }
  };

  const handleCompareVersions = async () => {
    if (selectedVersions.length !== 2) {
      toast.error('Please select exactly 2 versions to compare');
      return;
    }

    try {
      const response = await axios.get(`${API}/quotes/${quoteId}/versions/compare`, {
        params: {
          version_a: selectedVersions[0],
          version_b: selectedVersions[1]
        }
      });
      setComparison(response.data);
    } catch (error) {
      console.error('Failed to compare versions:', error);
      toast.error('Failed to compare versions');
    }
  };

  const toggleVersionSelection = (version) => {
    if (selectedVersions.includes(version)) {
      setSelectedVersions(selectedVersions.filter(v => v !== version));
    } else if (selectedVersions.length < 2) {
      setSelectedVersions([...selectedVersions, version]);
    } else {
      toast.error('You can only compare 2 versions at a time');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background border border-border rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-xl">
        {/* Header */}
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <History className="text-primary" size={24} />
            <div>
              <h2 className="text-2xl font-bold uppercase tracking-tight">Version History</h2>
              <p className="text-sm text-muted-foreground font-mono">
                Quote: {currentQuote?.quote_number} • {versions.length} versions
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowNewVersion(true)}
              className="font-mono uppercase text-xs"
            >
              <Plus size={16} className="mr-1" />
              Save Version
            </Button>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* New Version Form */}
          {showNewVersion && (
            <Card className="border-primary mb-6">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-mono uppercase tracking-wide flex items-center gap-2">
                  <Save size={16} />
                  Create New Version
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-xs font-mono uppercase">Reason for Change *</Label>
                  <Input
                    value={newVersionData.change_reason}
                    onChange={(e) => setNewVersionData({...newVersionData, change_reason: e.target.value})}
                    placeholder="e.g., Client requested price adjustment"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Summary of Changes</Label>
                  <Textarea
                    value={newVersionData.change_summary}
                    onChange={(e) => setNewVersionData({...newVersionData, change_summary: e.target.value})}
                    placeholder="Brief description of what changed..."
                    className="mt-1"
                    rows={2}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleCreateVersion}
                    disabled={saving}
                    className="flex-1"
                  >
                    {saving ? 'Saving...' : 'Create Version'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowNewVersion(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Comparison View */}
          {comparison && (
            <Card className="border-border mb-6 bg-muted/30">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-mono uppercase tracking-wide flex items-center gap-2">
                    <GitCompare size={16} />
                    Version Comparison
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setComparison(null);
                      setSelectedVersions([]);
                    }}
                  >
                    <X size={16} />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="p-3 bg-background rounded border border-border">
                    <p className="text-xs text-muted-foreground mb-1">Version {comparison.version_a.version}</p>
                    <p className="text-lg font-bold font-mono">{formatCurrency(comparison.version_a.total)}</p>
                    <p className="text-xs text-muted-foreground mt-1">{comparison.version_a.change_reason || 'No reason provided'}</p>
                  </div>
                  <div className="p-3 bg-background rounded border border-border">
                    <p className="text-xs text-muted-foreground mb-1">Version {comparison.version_b.version}</p>
                    <p className="text-lg font-bold font-mono">{formatCurrency(comparison.version_b.total)}</p>
                    <p className="text-xs text-muted-foreground mt-1">{comparison.version_b.change_reason || 'No reason provided'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="p-2 bg-background rounded border border-border">
                    <p className="text-xs text-muted-foreground">Total Change</p>
                    <p className={`text-lg font-bold font-mono ${comparison.total_change >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {comparison.total_change >= 0 ? '+' : ''}{formatCurrency(comparison.total_change)}
                    </p>
                    <p className="text-xs text-muted-foreground">({comparison.total_change_pct}%)</p>
                  </div>
                  <div className="p-2 bg-background rounded border border-border">
                    <p className="text-xs text-muted-foreground">Labour Change</p>
                    <p className={`font-bold font-mono ${comparison.labour_change >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {comparison.labour_change >= 0 ? '+' : ''}{formatCurrency(comparison.labour_change)}
                    </p>
                  </div>
                  <div className="p-2 bg-background rounded border border-border">
                    <p className="text-xs text-muted-foreground">Materials Change</p>
                    <p className={`font-bold font-mono ${comparison.materials_change >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {comparison.materials_change >= 0 ? '+' : ''}{formatCurrency(comparison.materials_change)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Compare Button */}
          {selectedVersions.length === 2 && !comparison && (
            <div className="mb-4">
              <Button onClick={handleCompareVersions} className="w-full">
                <GitCompare size={16} className="mr-2" />
                Compare Selected Versions ({selectedVersions.join(' vs ')})
              </Button>
            </div>
          )}

          {/* Version List */}
          {loading ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground font-mono">Loading versions...</p>
            </div>
          ) : versions.length === 0 ? (
            <div className="text-center py-8">
              <History className="mx-auto mb-3 text-muted-foreground/30" size={48} />
              <p className="text-muted-foreground">No versions saved yet</p>
              <p className="text-xs text-muted-foreground mt-1">Save a version to track changes over time</p>
            </div>
          ) : (
            <div className="space-y-3">
              {versions.map((version, idx) => (
                <div
                  key={version.id}
                  className={`p-4 border rounded-sm transition-colors ${
                    selectedVersions.includes(version.version)
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-muted-foreground/30'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedVersions.includes(version.version)}
                        onChange={() => toggleVersionSelection(version.version)}
                        className="mt-1"
                      />
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant={idx === 0 ? "default" : "secondary"}>
                            v{version.version}
                          </Badge>
                          {idx === 0 && (
                            <Badge variant="outline" className="text-green-500 border-green-500/30">
                              Latest
                            </Badge>
                          )}
                        </div>
                        <p className="font-medium text-sm">{version.change_reason || 'No reason provided'}</p>
                        {version.change_summary && (
                          <p className="text-xs text-muted-foreground mt-1">{version.change_summary}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock size={12} />
                            {new Date(version.created_at).toLocaleDateString()} {new Date(version.created_at).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                          </span>
                          <span className="flex items-center gap-1">
                            <User size={12} />
                            {version.created_by_name || 'Unknown'}
                          </span>
                          <span className="flex items-center gap-1">
                            <DollarSign size={12} />
                            {formatCurrency(version.snapshot?.total)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRestoreVersion(version.id, version.version)}
                      className="font-mono uppercase text-xs"
                      disabled={idx === 0}
                    >
                      <RotateCcw size={14} className="mr-1" />
                      Restore
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border bg-muted/30">
          <p className="text-xs text-muted-foreground text-center">
            Select 2 versions to compare changes • Click Restore to revert to a previous version
          </p>
        </div>
      </div>
    </div>
  );
};

export default QuoteVersionHistory;
